import React, { useState } from 'react';
import { Send, ArrowRight, MessageCircle, Clock, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { contactData } from '../data/mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email body with form data
    const emailSubject = `New Enquiry from ${formData.name} - ${formData.business || 'Website'}`;
    const emailBody = `Hi Kate,

New consultation request from your website:

Name: ${formData.name}
Email: ${formData.email}
Business: ${formData.business || 'Not specified'}

Message:
${formData.message}

---
Sent from your website contact form`;

    // Open email client with pre-filled data
    const mailtoLink = `mailto:katedigitallondon@gmail.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailtoLink;
    
    // Show success message
    setIsSubmitted(true);
    setFormData({ name: '', email: '', business: '', message: '' });
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  return (
    <section id="contact" className="section-padding bg-[#111111]">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Content */}
          <div>
            <span className="inline-block text-sm font-medium text-[#c9a962] tracking-wider uppercase mb-4">
              Get Started
            </span>
            
            <h2 className="heading-1 text-white mb-6">
              {contactData.title}
            </h2>
            
            <p className="body-large text-[#b3b3b3] mb-8">
              {contactData.description}
            </p>
            
            {/* Benefits */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-[#c9a962]" />
                <span className="body-regular text-[#b3b3b3]">Free initial consultation</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={20} className="text-[#c9a962]" />
                <span className="body-regular text-[#b3b3b3]">Response within 24 hours</span>
              </div>
              <div className="flex items-center gap-3">
                <MessageCircle size={20} className="text-[#c9a962]" />
                <span className="body-regular text-[#b3b3b3]">No commitment required</span>
              </div>
            </div>
            
            <div className="bg-[#141414] border border-[#2a2a2a] p-8 mb-8">
              <p className="heading-3 text-[#c9a962] italic">
                "{contactData.cta}"
              </p>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-4">
              <div>
                <span className="body-small block mb-1 text-[#808080]">Email</span>
                <a 
                  href="mailto:katedigitallondon@gmail.com" 
                  className="body-large text-white hover:text-[#c9a962] transition-colors"
                >
                  katedigitallondon@gmail.com
                </a>
              </div>
              <div>
                <span className="body-small block mb-1 text-[#808080]">Location</span>
                <p className="body-large text-white">London, United Kingdom</p>
              </div>
            </div>
          </div>
          
          {/* Form */}
          <div className="bg-[#141414] border border-[#2a2a2a] p-8 md:p-10">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 bg-[#c9a962] flex items-center justify-center mb-6">
                  <Send size={24} className="text-[#0a0a0a]" />
                </div>
                <h3 className="heading-2 text-white mb-4">Message Sent!</h3>
                <p className="body-large text-[#b3b3b3]">
                  Thanks for reaching out! I'll get back to you within 24 hours to discuss your project.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-6">
                  <h3 className="heading-3 text-white mb-2">Book a Free Consultation</h3>
                  <p className="body-small text-[#808080]">Tell me about your business and goals</p>
                </div>
                
                <div>
                  <Label htmlFor="name" className="body-regular text-white mb-2 block">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-[#0a0a0a] border-[#2a2a2a] text-white rounded-none h-12 focus:border-[#c9a962] focus:ring-[#c9a962] placeholder:text-[#808080]"
                    placeholder="Jane Smith"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="body-regular text-white mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-[#0a0a0a] border-[#2a2a2a] text-white rounded-none h-12 focus:border-[#c9a962] focus:ring-[#c9a962] placeholder:text-[#808080]"
                    placeholder="jane@yourbusiness.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="business" className="body-regular text-white mb-2 block">
                    Your Business
                  </Label>
                  <Input
                    id="business"
                    name="business"
                    type="text"
                    value={formData.business}
                    onChange={handleChange}
                    className="bg-[#0a0a0a] border-[#2a2a2a] text-white rounded-none h-12 focus:border-[#c9a962] focus:ring-[#c9a962] placeholder:text-[#808080]"
                    placeholder="Restaurant, Café, Beauty Clinic..."
                  />
                </div>
                
                <div>
                  <Label htmlFor="message" className="body-regular text-white mb-2 block">
                    What Are You Looking For? *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-[#0a0a0a] border-[#2a2a2a] text-white rounded-none focus:border-[#c9a962] focus:ring-[#c9a962] resize-none placeholder:text-[#808080]"
                    placeholder="E.g., I need Reels for my restaurant, looking to increase bookings..."
                  />
                </div>
                
                <Button 
                  type="submit"
                  className="w-full bg-[#c9a962] text-[#0a0a0a] hover:bg-[#d4b978] border-none rounded-none py-4 h-auto text-sm font-medium transition-all duration-300"
                >
                  <span className="flex items-center justify-center gap-2">
                    Send Message & Book Consultation
                    <ArrowRight size={16} />
                  </span>
                </Button>
                
                <p className="text-center body-small text-[#808080]">
                  No commitment • Free consultation • Response within 24 hours
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
